// :cow: Cosmose CONFIDENTIAL :iso:
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import {
  authChangePassword,
  authChangePasswordFailure,
  authChangePasswordSuccess,
  authLoginByCredentials,
  authLoginByCredentialsSuccess,
  authLoginSuccess,
  authLogout,
  authReauthorize,
  authRefreshToken,
  authResetPassword,
  authResetPasswordFailed,
  authResetPasswordSuccess,
} from './auth.actions';
import { AuthRepository } from '../shared/auth.repository';
import { AuthService } from '../shared/auth.service';
import { Router } from '@angular/router';
import { SpinnerService } from '@med/app/core/services/spinner.service';
import { AlertService } from '@med/app/core/services/alert.service';
import { errorMsgHandler } from '@med/utils';
import { decodeToken } from '@med/app/core/utils/decode-token';

@Injectable()
export class AuthEffects {

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authLogout),
      tap(() => {
        this.spinnerService.hide();
        this.authService.clearTokens();
        this.router.navigateByUrl('/login');
      }),
    ), {dispatch: false},
  );

  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authResetPassword),
      map(({username}) => username),
      mergeMap((username) => {
        this.spinnerService.show();
        return this.authRepository.resetPassword(username)
          .pipe(
            tap(() => this.spinnerService.hide()),
            mergeMap(() => of(authResetPasswordSuccess({username}))),
            catchError(error => {
              this.spinnerService.hide();

              return of(authResetPasswordFailed({error: error.error.message}));
            }),
          );
      }),
    ),
  );

  authResetPasswordSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authResetPasswordSuccess),
      tap((action) => {
        const extras = {queryParams: {username: action.username}};
        this.router.navigate(['/reset-password/new-password'], extras);
      }),
    ), {dispatch: false},
  );

  changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authChangePassword),
      mergeMap((data) => {
        this.spinnerService.show();
        return this.authRepository.changePassword(data.username, data.currentPassword, data.newPassword)
          .pipe(
            tap((() => this.spinnerService.hide())),
            mergeMap(() => of(authChangePasswordSuccess())),
            catchError(error => {
              this.spinnerService.hide();
              let message;
              if (error.status === 401) {
                message = error.error.message;
              } else {
                message = errorMsgHandler(error);
              }

              return of(authChangePasswordFailure({error: message}));
            }));
      }),
    ),
  );

  changePasswordSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authChangePasswordSuccess),
      tap(() => this.router.navigateByUrl('/reset-password/password-changed')),
    ), {dispatch: false},
  );

  getRefreshToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authRefreshToken),
      tap((authData) => this.authService.authorize(authData.authData)),
      catchError(() => this.router.navigateByUrl('/login')),
    ), {dispatch: false},
  );

  reauthorize$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authReauthorize),
      mergeMap(() => this.authRepository.reauthorize(this.authService.getRefreshToken())
        .pipe(
          tap((response) => this.authService.authorize(response)),
          map((response) => authLoginSuccess({profile: decodeToken(response.access_token)}),
          ),
        )),
    ), {dispatch: true},
  );

  authLoginByCredentials$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authLoginByCredentials),
      mergeMap((action) => this.authRepository.authorize({password: action.password, username: action.login})
        .pipe(
          map((response) => authLoginByCredentialsSuccess({authData: response})),
        )),
    ), {dispatch: true},
  );

  authLoginByCredentialsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authLoginByCredentialsSuccess),
      tap((action) => {
        this.authService.authorize(action.authData);
        this.router.navigateByUrl('/');
      }),
    ), {dispatch: false},
  );

  changePasswordFailure$ = createEffect(() => this.actions$.pipe(
    ofType(authChangePasswordFailure),
    tap((action) => this.alertService.error(action.error)),
    ), {dispatch: false},
  );

  authResetPasswordFailed$ = createEffect(() => this.actions$.pipe(
    ofType(authResetPasswordFailed),
    tap((action) => this.alertService.error(action.error)),
    ), {dispatch: false},
  );

  constructor(
    private actions$: Actions,
    private authRepository: AuthRepository,
    private authService: AuthService,
    private router: Router,
    private alertService: AlertService,
    private spinnerService: SpinnerService,
  ) {
  }
}
